import React from 'react';
import Client from './components/Client';
import Config from './components/ClientConfig';
import { useSearchParams } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { Box, Stack } from '@mui/material';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import {
  MobileInfoRow,
  MobileStandalonePage,
  MobileSurface,
} from './components/MobileStandalone';
import { useDataStore } from './dataStore';

function ValidateEmail() {
  const client = React.useMemo(() => new Client("none"), []);
  const { t, languageReady } = useDataStore();
  const [urlParams] = useSearchParams();
  const email = urlParams.get('email');
  const token = urlParams.get('token');
  const hasLanguageParam = Boolean(urlParams.get('language') || urlParams.get('lang'));
  const shouldWaitForQueryLanguage = hasLanguageParam && !languageReady;
  const hasRequiredParams = Boolean(email && token);
  const [data, setData] = React.useState(null);
  const submittedRef = React.useRef("");

  React.useEffect(() => {
    if (!hasRequiredParams) {
      setData({ message: t("mobile.invalidEmailLink") });
      return;
    }

    const requestKey = `${email}:${token}`;
    if (submittedRef.current === requestKey) return;
    submittedRef.current = requestKey;

    const queryData = { email, token };
    const response = client.pushData(Config.VALIDATE_ACCOUNT_API_URL, queryData);
    setData(response || { message: t("mobile.emailVerificationFailed") });
  }, [client, email, token, hasRequiredParams, t]);

  const statusMessage = data?.message || t("mobile.emailChecking");

  return (
    <MobileStandalonePage>
      <Box sx={{ width: "100%", pt: { xs: 5, sm: 7 } }}>
        <MobileSurface
          sx={{
            p: { xs: 2.25, sm: 2.75 },
            background: "linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(255,247,237,0.95) 100%)",
            border: "1px solid rgba(251,146,60,0.22)",
            boxShadow: "0 22px 54px rgba(124,45,18,0.14)",
          }}
        >
          <Stack spacing={2}>
            {shouldWaitForQueryLanguage ? (
              <Box sx={{ minHeight: 180 }} />
            ) : (
              <>
                <Box sx={{ textAlign: "center" }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      mx: "auto",
                      mb: 1.2,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#9a3412",
                      background: "linear-gradient(135deg, #fff7ed 0%, #fed7aa 72%, #fdba74 100%)",
                      border: "1px solid rgba(124,45,18,0.12)",
                      boxShadow: "0 12px 28px rgba(194,65,12,0.12)",
                    }}
                  >
                    <MarkEmailReadRoundedIcon />
                  </Box>

                  <Typography
                    component="h1"
                    sx={{
                      color: "#431407",
                      fontSize: 24,
                      fontWeight: 950,
                      lineHeight: 1.15,
                      letterSpacing: 0,
                    }}
                  >
                    {t("mobile.validateEmailTitle")}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.75,
                      color: "rgba(124,45,18,0.72)",
                      fontSize: 14,
                      lineHeight: 1.45,
                      fontWeight: 650,
                    }}
                  >
                    {t("mobile.validateEmailIntro")}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    px: 1.25,
                    pt: 1.2,
                    borderRadius: "8px",
                    bgcolor: "rgba(255,237,213,0.46)",
                    border: "1px solid rgba(251,146,60,0.16)",
                  }}
                >
                  <MobileInfoRow label="Email">
                    {email || t("mobile.emailUnavailable")}
                  </MobileInfoRow>
                </Box>

                <Typography
                  sx={{
                    color: "#7c2d12",
                    fontSize: 16,
                    fontWeight: 850,
                    lineHeight: 1.35,
                    textAlign: "center",
                  }}
                >
                  {statusMessage}
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(124,45,18,0.66)",
                    fontSize: 13,
                    lineHeight: 1.5,
                    textAlign: "center",
                  }}
                >
                  {t("mobile.closeWhenFinished")}
                </Typography>
              </>
            )}
          </Stack>
        </MobileSurface>
      </Box>
    </MobileStandalonePage>
  );
}

export default ValidateEmail;
